import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, Play, Square, Wand2, X } from "lucide-react";
import { api, formatDate, type AnwendenStatus, type AnwendenVorschau, type Folder } from "../api";
import { fehlerText, toast, useDialog } from "./verwaltung";

interface Props {
  folders: Folder[];
  /** Nur diese Regel; ohne Angabe alle aktiven. */
  rule?: { id: number; name: string };
  /** Nur diesen Ordner prüfen — aus dem Ordnermenü heraus. */
  ordner?: Folder;
  /** Mehrere Ordner vorwählen, etwa „Posteingang samt Unterordnern" aus einer Ansicht. */
  ordnerMenge?: { titel: string; ids: number[] };
  onClose: () => void;
  onDone: () => void;
}

/** Standardmäßig ausgenommen — dort liegt nichts, was Regeln sortieren sollten. */
const AUSSEN = new Set(["\\Sent", "\\Drafts", "\\Trash", "\\Junk"]);
const ANZEIGE_PRO_GRUPPE = 60;

function ordnerLabel(f: Folder): string {
  const namen: Record<string, string> = {
    "\\Inbox": "Posteingang", "\\Sent": "Gesendet", "\\Drafts": "Entwürfe",
    "\\Archive": "Archiv", "\\Junk": "Unerwünscht", "\\Trash": "Papierkorb",
  };
  return (f.special_use && namen[f.special_use]) || (f.path.toUpperCase() === "INBOX" ? "Posteingang"
    : f.delimiter ? f.path.split(f.delimiter).join(" / ") : f.path);
}

/**
 * Regeln auf den Bestand anwenden: erst Vorschau, gruppiert nach Regel, dann
 * gezielt abwählen, dann ausführen. Der Lauf passiert im Hintergrund; der
 * Dialog zeigt den Fortschritt und kann abbrechen.
 */
export function RegelnAnwenden({ folders, rule, ordner: nurOrdner, ordnerMenge, onClose, onDone }: Props) {
  const box = useRef<HTMLDivElement | null>(null);
  const [ordner, setOrdner] = useState<Set<number>>(
    () => nurOrdner
      ? new Set([nurOrdner.id])
      : ordnerMenge
        ? new Set(ordnerMenge.ids)
        : new Set(folders.filter((f) => !f.special_use || !AUSSEN.has(f.special_use)).map((f) => f.id)),
  );
  // Vorgewählt heißt: gleich die Vorschau zeigen, nicht erst klicken lassen.
  const sofort = Boolean(nurOrdner || ordnerMenge);
  const [belege, setBelege] = useState(true);
  const [vorschau, setVorschau] = useState<AnwendenVorschau | null>(null);
  const [auswahl, setAuswahl] = useState<Set<number>>(new Set());
  const [offen, setOffen] = useState<number | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [status, setStatus] = useState<AnwendenStatus | null>(null);
  const [stoppt, setStoppt] = useState(false);
  const timer = useRef<number | null>(null);
  const anfrage = useRef(0);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const laeuft = status?.laeuft ?? false;
  const fertig = Boolean(status && !status.laeuft);
  // Schließen ist auch während des Laufs erlaubt — er läuft im Hintergrund weiter.
  useDialog(box, onClose, !laedt);

  const optionen = { ruleIds: rule ? [rule.id] : undefined, folderIds: [...ordner], belege };

  const pruefen = async () => {
    const nr = ++anfrage.current;
    setLaedt(true);
    setFehler(null);
    try {
      const v = await api.anwendenVorschau(optionen);
      if (nr !== anfrage.current) return;
      setVorschau(v);
      setAuswahl(new Set(v.gruppen.flatMap((g) => g.treffer.map((t) => t.id))));
      setOffen(v.gruppen.length === 1 ? v.gruppen[0]!.ruleId : null);
    } catch (e) {
      if (nr === anfrage.current) setFehler(fehlerText(e));
    } finally {
      if (nr === anfrage.current) setLaedt(false);
    }
  };

  const beobachten = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(async () => {
      const s = await api.anwendenStatus().catch(() => null);
      if (!s) return;
      setStatus(s);
      if (!s.laeuft && timer.current) {
        window.clearInterval(timer.current);
        timer.current = null;
        setStoppt(false);
        doneRef.current();
      }
    }, 1000);
  };

  // Einen bereits laufenden Durchgang (etwa nach Neuladen) sofort anzeigen;
  // aus dem Ordnermenü heraus gleich die Vorschau erstellen.
  useEffect(() => {
    api.anwendenStatus().then((s) => {
      if (s.laeuft) { setStatus(s); beobachten(); }
      else if (sofort) void pruefen();
    }).catch(() => { if (sofort) void pruefen(); });
    return () => { anfrage.current++; if (timer.current) window.clearInterval(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const starten = async () => {
    setFehler(null);
    try {
      const r = await api.anwendenStarten([...auswahl], optionen);
      setStatus({ laeuft: true, gesamt: r.gesamt, erledigt: 0, verschoben: 0, belege: 0, fehler: 0, letzterFehler: null, gestartet: null, beendet: null });
      beobachten();
    } catch (e) {
      setFehler(fehlerText(e));
    }
  };

  const stoppen = async () => {
    setStoppt(true);
    try {
      await api.anwendenStop();
    } catch (e) {
      setStoppt(false);
      toast.fehler(e);
    }
  };

  const schliessen = () => {
    if (laeuft) toast.info("Der Durchgang läuft im Hintergrund weiter. Den Stand siehst du, wenn du den Dialog wieder öffnest.");
    onClose();
  };

  const ordnerUmschalten = (id: number) => {
    anfrage.current++;
    setLaedt(false);
    setVorschau(null);
    setOrdner((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const gruppeUmschalten = (ids: number[], an: boolean) =>
    setAuswahl((s) => { const n = new Set(s); for (const id of ids) { if (an) n.add(id); else n.delete(id); } return n; });

  const umschalten = (id: number) =>
    setAuswahl((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const sortierteOrdner = useMemo(() => [...folders].sort((a, b) => ordnerLabel(a).localeCompare(ordnerLabel(b), "de")), [folders]);
  const alleIds = useMemo(() => vorschau?.gruppen.flatMap((g) => g.treffer.map((t) => t.id)) ?? [], [vorschau]);
  const anteil = status && status.gesamt ? Math.round((status.erledigt / status.gesamt) * 100) : 0;
  const abgebrochen = fertig && status && status.erledigt < status.gesamt;
  const titel = rule ? `„${rule.name}" auf vorhandene Mails anwenden`
    : nurOrdner ? `„${ordnerLabel(nurOrdner)}" neu zuordnen`
    : ordnerMenge ? `„${ordnerMenge.titel}" neu einsortieren`
    : "Regeln auf vorhandene Mails anwenden";

  return (
    <div className="sheet-backdrop" onClick={(e) => e.target === e.currentTarget && !laedt && schliessen()}>
      <div ref={box} className="sheet leeren" role="dialog" aria-modal="true" aria-labelledby="anwenden-titel">
        <div className="sheet-head">
          <Wand2 size={16} aria-hidden="true" style={{ color: "var(--accent)", flexShrink: 0 }} />
          <span id="anwenden-titel" style={{ flex: 1, minWidth: 0 }}>{titel}</span>
          <button className="icon-btn" onClick={schliessen} aria-label="Dialog schließen" title="Schließen (Esc)">
            <X aria-hidden="true" />
          </button>
        </div>

        <div className="sheet-body">
          {status ? (
            <div className="v-fortschritt" data-laeuft={laeuft} role="status" aria-live="polite">
              <div className="v-fortschritt-kopf">
                <strong>
                  {laeuft ? (stoppt ? "Wird abgebrochen …" : "Läuft …") : abgebrochen ? "Abgebrochen" : "Fertig"}
                </strong>
                <span className="v-prozent">{anteil} %</span>
              </div>
              <div className="bar-track" role="progressbar" aria-valuemin={0} aria-valuemax={status.gesamt} aria-valuenow={status.erledigt}
                aria-label="Fortschritt">
                <span className="bar-fill" style={{ width: `${anteil}%` }} />
              </div>
              <p className="v-fortschritt-zeile">
                <strong>{status.erledigt.toLocaleString("de-DE")}</strong> von {status.gesamt.toLocaleString("de-DE")} bearbeitet
                {" · "}{status.verschoben.toLocaleString("de-DE")} verschoben{" · "}{status.belege} {status.belege === 1 ? "Beleg" : "Belege"} abgelegt
                {status.fehler > 0 && <> · <span style={{ color: "var(--danger)" }}>{status.fehler} Fehler</span></>}
              </p>
              {status.letzterFehler && <p className="fail-line">Letzter Fehler: {status.letzterFehler}</p>}
              {fertig && !abgebrochen && <p className="ok-line">Verschobene Mails sind in ihren Zielordnern schon wieder eingelesen.</p>}
              {abgebrochen && <p className="hint">Was bis zum Abbruch erledigt war, bleibt so. Den Rest kannst du jederzeit neu anstoßen.</p>}
            </div>
          ) : (
            <>
              {!nurOrdner && (
                <div className="field">
                  <label id="anwenden-ordner">In diesen Ordnern suchen</label>
                  <div className="folder-picker" role="group" aria-labelledby="anwenden-ordner">
                    {sortierteOrdner.map((f) => (
                      <label key={f.id} className="chip pick" data-on={ordner.has(f.id)}>
                        <input type="checkbox" checked={ordner.has(f.id)} onChange={() => ordnerUmschalten(f.id)} />
                        {ordnerLabel(f)}
                        {f.total ? <span style={{ color: "var(--ink-3)" }}>{f.total.toLocaleString("de-DE")}</span> : null}
                      </label>
                    ))}
                  </div>
                  <p className="hint">Gesendet, Entwürfe, Papierkorb und Unerwünscht sind anfangs abgewählt.</p>
                </div>
              )}
              <label className="v-check" style={{ marginBottom: 16 }}>
                <input type="checkbox" checked={belege} onChange={(e) => { anfrage.current++; setLaedt(false); setVorschau(null); setBelege(e.target.checked); }} />
                Auch Belege einsammeln — Rechnungen ablegen, die noch nicht im Belegordner liegen
              </label>

              {laedt ? (
                <div className="v-laden" role="status">Prüfe Mails …</div>
              ) : !vorschau ? (
                <div className="v-leer">
                  <strong>Erst ansehen, dann ausführen</strong>
                  <span>Die Vorschau zeigt je Regel, welche Mails sich ändern würden. Du kannst danach einzelne abwählen.</span>
                  <button className="btn primary" onClick={() => void pruefen()} disabled={ordner.size === 0}>
                    Vorschau erstellen
                  </button>
                  {ordner.size === 0 && <span className="hint">Bitte mindestens einen Ordner wählen.</span>}
                </div>
              ) : vorschau.betroffen === 0 ? (
                <div className="v-leer">
                  <strong>Nichts zu tun</strong>
                  <span>{vorschau.geprueft.toLocaleString("de-DE")} Mails geprüft — alles ist schon so, wie die Regeln es wollen.</span>
                </div>
              ) : (
                <>
                  <div className="v-kacheln">
                    <div className="v-kachel"><span className="n">{vorschau.betroffen.toLocaleString("de-DE")}</span><span className="k">von {vorschau.geprueft.toLocaleString("de-DE")} betroffen</span></div>
                    <div className="v-kachel"><span className="n">{vorschau.zaehler.verschieben}</span><span className="k">werden verschoben</span></div>
                    <div className="v-kachel"><span className="n">{vorschau.zaehler.kategorie}</span><span className="k">neu eingeordnet</span></div>
                    <div className="v-kachel" data-art={vorschau.zaehler.spam ? "gefahr" : undefined}><span className="n">{vorschau.zaehler.spam}</span><span className="k">nach Unerwünscht</span></div>
                    <div className="v-kachel"><span className="n">{vorschau.zaehler.belege}</span><span className="k">Belege</span></div>
                  </div>

                  <div className="v-reihe" style={{ marginBottom: 8 }}>
                    <span className="hint" style={{ margin: 0 }}>{auswahl.size.toLocaleString("de-DE")} von {alleIds.length.toLocaleString("de-DE")} ausgewählt</span>
                    <div className="spacer" />
                    <button className="btn quiet" onClick={() => setAuswahl(new Set(alleIds))} disabled={auswahl.size === alleIds.length}>Alle</button>
                    <button className="btn quiet" onClick={() => setAuswahl(new Set())} disabled={auswahl.size === 0}>Keine</button>
                  </div>

                  <div className="anwenden-gruppen">
                    {vorschau.gruppen.map((g) => {
                      const ids = g.treffer.map((t) => t.id);
                      const gewaehlt = ids.filter((id) => auswahl.has(id)).length;
                      const auf = offen === g.ruleId;
                      return (
                        <div key={g.ruleId} className="rule">
                          <div className="rule-head">
                            <input
                              type="checkbox"
                              checked={ids.length > 0 && gewaehlt === ids.length}
                              ref={(el) => { if (el) el.indeterminate = gewaehlt > 0 && gewaehlt < ids.length; }}
                              onChange={(e) => gruppeUmschalten(ids, e.target.checked)}
                              aria-label={`Alle Mails der Regel „${g.name}" einbeziehen`}
                            />
                            <button type="button" className="v-gruppe-knopf" aria-expanded={auf} onClick={() => setOffen(auf ? null : g.ruleId)}>
                              <ChevronRight className="v-aufklapp" data-offen={auf} aria-hidden="true" />
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span className="rule-name" style={{ display: "block" }}>{g.name}</span>
                                <span className="rule-summary" style={{ display: "block" }}>{g.treffer[0]?.aenderungen.join(" · ")}</span>
                              </span>
                              <span className="chip">{gewaehlt} / {g.anzahl}</span>
                            </button>
                          </div>
                          {auf && (
                            <ul className="leeren-liste" style={{ border: 0, borderTop: "1px solid var(--line)", borderRadius: 0 }}>
                              {g.treffer.slice(0, ANZEIGE_PRO_GRUPPE).map((t) => (
                                <li key={t.id} className="leeren-zeile">
                                  <label className="leeren-retten" title="Diese Mail einbeziehen">
                                    <input type="checkbox" checked={auswahl.has(t.id)} onChange={() => umschalten(t.id)}
                                      style={{ accentColor: "var(--accent)" }} aria-label={`„${t.subject || "ohne Betreff"}" einbeziehen`} />
                                  </label>
                                  <div className="leeren-inhalt">
                                    <span className="msg-row">
                                      <span className="msg-from">{t.from}</span>
                                      <span className="msg-date">{formatDate(t.date)}</span>
                                    </span>
                                    <span className="msg-subject" style={{ display: "block" }}>{t.subject || "(ohne Betreff)"}</span>
                                    <span className="msg-meta">
                                      <span className="chip">{t.folder.replace(/\./g, " / ")}</span>
                                      {t.aenderungen.map((a) => <span key={a} className="chip solid">{a}</span>)}
                                    </span>
                                  </div>
                                </li>
                              ))}
                              {g.treffer.length > ANZEIGE_PRO_GRUPPE && (
                                <li className="hint" style={{ padding: "10px 14px", margin: 0 }}>
                                  … und {g.treffer.length - ANZEIGE_PRO_GRUPPE} weitere mit denselben Änderungen (über das Kästchen der Gruppe mit an- und abwählbar).
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
          {fehler && <p className="error" role="alert">{fehler}</p>}
        </div>

        <div className="sheet-foot">
          {laeuft ? (
            <>
              <span className="hint" style={{ flex: 1 }}>Läuft im Hintergrund weiter, auch wenn du den Dialog schließt.</span>
              <button className="btn quiet" onClick={schliessen}>Ausblenden</button>
              <button className="btn danger" onClick={() => void stoppen()} disabled={stoppt}>
                <Square aria-hidden="true" /> {stoppt ? "Bricht ab …" : "Abbrechen"}
              </button>
            </>
          ) : fertig ? (
            <>
              <div className="spacer" />
              <button className="btn primary" onClick={onClose}>Schließen</button>
            </>
          ) : vorschau && vorschau.betroffen > 0 ? (
            <>
              <span className="hint" style={{ flex: 1 }}>
                Wird wirklich ausgeführt — auch im Testbetrieb. Jede Änderung steht im Protokoll unter Auswertung.
              </span>
              <button className="btn quiet" onClick={() => void pruefen()} disabled={laedt}>Neu prüfen</button>
              <button className="btn primary" onClick={() => void starten()} disabled={auswahl.size === 0}>
                <Play aria-hidden="true" /> {auswahl.size.toLocaleString("de-DE")} {auswahl.size === 1 ? "Mail" : "Mails"} ändern
              </button>
            </>
          ) : (
            <>
              <div className="spacer" />
              <button className="btn quiet" onClick={onClose}>Schließen</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
